/**
 * Company Document entity DTOs.
 *
 * Mirrors Prisma `CompanyDocument` (prisma/schema.prisma:2620) — the
 * company-wide document store (insurance, policies, certs) with optional
 * expiry tracking. Scoped per `userId`, matching the web
 * `/api/company-documents/*` routes.
 *
 * The mobile mirror at `/api/mobile/v1/company-documents/*` consumes these
 * via `safeParseOrWarn`.
 *
 * `fileUrl` carries BOTH key classes, so do not assume it is openable: the
 * `/upload` sub-route writes a public-class `company-docs/{userId}/…` key, but
 * the presigned-upload pipeline writes `private/companies/…` and both POST
 * routes accept it (`validateOwnedStoredFileUrl`). A `private/` object 403s
 * unless it is signed. The web readers resolve it (#2326,
 * `lib/company-documents-read.ts`); the mobile serialiser still returns it raw
 * — tracked as a follow-up, not a property of this schema.
 */
import { z } from "zod";

export const CompanyDocumentCategorySchema = z.enum([
  "Insurance",
  "Policy",
  "Certification",
  "Other",
]);

export type CompanyDocumentCategory = z.infer<
  typeof CompanyDocumentCategorySchema
>;

export const CompanyDocumentSchema = z.object({
  id: z.string(),
  label: z.string(),
  category: CompanyDocumentCategorySchema,
  fileUrl: z.string(),
  fileName: z.string(),
  expiresAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CompanyDocument = z.infer<typeof CompanyDocumentSchema>;

/**
 * Server-only strict variant of the response DTO (#2411 batch 1).
 *
 * The mobile app parses `CompanyDocumentSchema` off the wire via
 * `safeParseOrWarn`, so it is deliberately NOT `.strict()` — an installed
 * build must tolerate a field the server adds later. The server's
 * `serialiseCompanyDocument` builds the payload field-by-field and
 * `.parse()`s it as an over-serialisation guard, which is where the
 * strictness is re-applied. Request bodies are unaffected: they are
 * validated by the separate Create/Update schemas below, which stay strict.
 */
export const CompanyDocumentStrict = CompanyDocumentSchema.strict();

export const CompanyDocumentCreateRequest = z
  .object({
    label: z.string().min(1),
    category: CompanyDocumentCategorySchema,
    fileUrl: z.string().min(1),
    fileName: z.string().min(1),
    expiresAt: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .strict();

export type CompanyDocumentCreateRequest = z.infer<
  typeof CompanyDocumentCreateRequest
>;

export const CompanyDocumentUpdateRequest = z
  .object({
    label: z.string().min(1).optional(),
    category: CompanyDocumentCategorySchema.optional(),
    expiresAt: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .strict();

export type CompanyDocumentUpdateRequest = z.infer<
  typeof CompanyDocumentUpdateRequest
>;
